import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import cn from 'classnames';
import { nanoid } from 'nanoid';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProgressBar } from 'react-step-progress-bar';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { useSensors } from '@webapp/common/components/sortable';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { calcPercent } from '@webapp/common/lib/ui';
import { QuestionParamsCardSortingType } from '@webapp/common/resources/question';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { excludeHiddenAnswers } from '@webapp/survey/src/machine/helpers/logic';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { Card, Cards, Category, CreatedCategory, NewCategory } from './card-sorting-content';
import type { ICard } from './helpers';

import css from './card-sorting.css';

export const CardSorting: FC<SurveyQuestion> = ({ question }) => {
    const {
        id,
        responseActor,
        answersActors,
        params: { cardSortingType },
        categories,
    } = question;
    const sensors = useSensors(200);
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const styles = useContext(CustomStylesCtx);
    const rootRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [activeId, setActiveId] = useState(null);
    const [isFullHeight, setIsFullHeight] = useState(false);
    const [sortedCards, setSortedCards] = useState<Record<string, string>>({});
    const [categoriesCards, setCategoriesCards] = useState<Record<string, Array<ICard>>>({});
    const [createdCategories, setCreatedCategories] = useState<Array<{ id: string; name: string }>>([]);

    const {
        design: {
            fonts: { elementColor },
        },
        params: {
            alert: {
                cardSortingText,
                cardSortingNewCategoryText,
                cardSortingCardsText,
                cardSortingCategoryNameText,
                cardSortingNoCategoryAvailableText,
            },
        },
    } = useStateSelector(selectInfo);
    const filteredAnswers = excludeHiddenAnswers(answersActors);

    const { cards, cardsById }: { cards: Array<ICard>; cardsById: Record<string, ICard> } = useMemo(() => {
        const result = {
            cards: [],
            cardsById: {},
        };
        filteredAnswers.forEach((a) => {
            const { id, file, value } = a.getSnapshot().context;
            const card = { id, file: file?.path, text: pipingAnswersReplacer(value[0]) };

            result.cards.push(card);
            result.cardsById[id] = card;
        });

        return result;
    }, [filteredAnswers]);
    const findActiveCard = cards.find((item) => item.id === activeId);
    const isWithClickReply = Boolean(document.querySelector('.survey-with-click-reply'));
    const bundle = rootRef?.current?.closest('.currentBundleNew');

    useEffect(() => {
        return () => {
            bundle?.classList.remove('currentBundleNewFullHeight');
        };
    }, []);

    useEffect(() => {
        if (isWithClickReply && bundle && !bundle.classList.contains('currentBundleNewFullHeight')) {
            bundle.classList.add('currentBundleNewFullHeight');
            setIsFullHeight(true);
        }
    });

    useEffect(() => {
        const newCategoriesCards = {};

        Object.entries(sortedCards).map(([cardId, categoryId]) => {
            if (newCategoriesCards[categoryId]) {
                newCategoriesCards[categoryId].push(cardsById[cardId]);
            } else {
                newCategoriesCards[categoryId] = [cardsById[cardId]];
            }
        });

        setCategoriesCards(newCategoriesCards);
    }, [sortedCards]);

    const addCategory = (activeId: number) => {
        const newCategoryId = nanoid();

        setCreatedCategories((prev) => {
            const result = [...prev, { id: newCategoryId, name: '' }];
            responseActor.send({ type: 'CHANGE_EXTRA', value: result as unknown as string });
            return result;
        });
        setSortedCards((prev) => {
            const result = { ...prev, [activeId]: newCategoryId };
            responseActor.send({ type: 'CHANGE', value: result as unknown as string });
            return result;
        });
    };

    const onChangeCategory = (id: string, name: string) => {
        setCreatedCategories((prev) => {
            const result = prev.map((item) => (item.id === id ? { ...item, name } : item));
            responseActor.send({ type: 'CHANGE_EXTRA', value: result as unknown as string });
            return result;
        });
    };

    const onRemoveCategory = (id: string) => {
        if (categoriesCards[id]?.length) {
            setSortedCards((prev) => {
                const result = { ...prev };

                Object.entries(prev).forEach(([key, value]) => {
                    if (value === id) {
                        delete result[key];
                    }
                });
                responseActor.send({ type: 'CHANGE', value: result as unknown as string });

                return result;
            });
        }

        setCreatedCategories((prev) => {
            const result = prev.filter((item) => item.id !== id);
            responseActor.send({ type: 'CHANGE_EXTRA', value: result as unknown as string });
            return result;
        });
    };

    const getPercent = useCallback(
        () => calcPercent(Object.keys(sortedCards).length, cards.length),
        [sortedCards, cards],
    );

    const renderCategories = () => {
        let content;

        if (!categories.length && !createdCategories.length) {
            if (cardSortingType === QuestionParamsCardSortingType.OPEN) {
                if (!activeId) {
                    content = cardSortingNewCategoryText;
                }
            } else {
                content = cardSortingNoCategoryAvailableText;
            }
        } else {
            content = (
                <>
                    {categories
                        .filter((item) => !item.hidden)
                        .map(({ id, name }) => {
                            return (
                                <Category
                                    id={id}
                                    name={pipingAnswersReplacer(name)}
                                    key={id}
                                    cards={categoriesCards[id]}
                                    descriptionText={cardSortingText}
                                />
                            );
                        })}
                    {createdCategories.map(({ id, name }) => {
                        return (
                            <CreatedCategory
                                id={id}
                                name={name}
                                key={id}
                                cards={categoriesCards[id]}
                                onChange={onChangeCategory}
                                onRemove={onRemoveCategory}
                                descriptionText={cardSortingText}
                                cardSortingCategoryNameText={cardSortingCategoryNameText}
                            />
                        );
                    })}
                </>
            );
        }

        return (
            <div className={cn(css.categories, { [css.categoriesFullHeight]: isFullHeight })}>
                {content}
                {Boolean(activeId) && cardSortingType === QuestionParamsCardSortingType.OPEN && (
                    <NewCategory descriptionText={cardSortingNewCategoryText} />
                )}
            </div>
        );
    };

    return (
        <div
            className={cn(CssQuestionType.QUESTION, CssQuestionType.CARD_SORTING, css.cardSortingWrap, {
                [css.cardSortingWrapFullHeight]: isWithClickReply,
            })}
            ref={rootRef}
        >
            <div className={css.progressBarWrap}>
                <span>{cardSortingCardsText}</span>
                <div className={css.progressBarContainer}>
                    <span>
                        {Object.keys(sortedCards).length} / {cards.length}
                    </span>
                    <div className={css.progressBar}>
                        <ProgressBar percent={getPercent()} unfilledBackground='#ccc' filledBackground={elementColor} />
                    </div>
                </div>
            </div>

            <div
                className={css.cardSorting}
                ref={wrapRef}
                style={{
                    // @ts-ignore
                    '--over-color': styles?.fieldBorderColor50,
                    '--category-color': styles?.fieldBorderColor30,
                }}
            >
                <DndContext
                    collisionDetection={closestCenter}
                    sensors={sensors}
                    onDragStart={(event) => {
                        if (wrapRef && wrapRef.current) {
                            wrapRef.current.style.height = `${wrapRef?.current?.offsetHeight}px`;
                        }
                        setActiveId(event.active.id);
                    }}
                    onDragEnd={(event) => {
                        if (wrapRef && wrapRef.current) {
                            wrapRef.current.style.height = '';
                        }
                        const overId = event?.over?.id;

                        if (overId) {
                            if (overId === 'newCategory') {
                                addCategory(activeId);
                            } else {
                                setSortedCards((prev) => {
                                    if (overId === 'cards') {
                                        const newState = { ...prev };
                                        delete newState[activeId];

                                        responseActor.send({
                                            type: 'CHANGE',
                                            value: newState as unknown as string,
                                            questionId: id,
                                        });

                                        return newState;
                                    }

                                    const result = { ...prev, [activeId]: overId };
                                    responseActor.send({ type: 'CHANGE', value: result as string, questionId: id });

                                    return result;
                                });
                            }
                        }

                        setActiveId(null);
                    }}
                >
                    <Cards cards={cards} sortedCards={sortedCards} isFullHeight={isFullHeight} />
                    {renderCategories()}
                    {activeId &&
                        createPortal(
                            <DragOverlay>
                                {findActiveCard ? <Card text={findActiveCard.text} file={findActiveCard.file} /> : null}
                            </DragOverlay>,
                            document.body,
                        )}
                </DndContext>
            </div>
        </div>
    );
};
